import type { FC } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import { Input, InputWithValidation, Label, Radio, RadioGroup, Select } from '@skytech/ui'
import {
  COUNTRY_FLAGS,
  countryOptions,
  getFormattedPhoneNumber,
  isOnlyLetters,
  isPostcodeInRange,
  MAX_PHONE_NUMBER_LENGTH,
} from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { Product } from '@/modules/ecom-policy/types'
import employeeResources from '@/resources/employees'
import { commonQueries } from '@/resources/queries'

const CUSTOM_ADDRESS_ID = -1

function getTransportationProduct({
  transportationProducts,
  selectedPostcode,
}: {
  transportationProducts: Product[]
  selectedPostcode: string
}) {
  return transportationProducts.find(product =>
    isPostcodeInRange({ postcode: selectedPostcode, rangeString: product.name })
  )
}

const SectionAddress: FC = () => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const shopCtx = useShopNewOrderContext()
  const [selectedAddressId, setSelectAddressId] = shopCtx.selectedAddressId
  const [customAddress, setCustomAddress] = shopCtx.customAddress
  const [isPrivateDeliveryAddress, setIsPrivateDeliveryAddress] = shopCtx.isPrivateDeliveryAddress
  const [costObjectId] = shopCtx.costObjectId
  const [contactName, setContactName] = shopCtx.contactName
  const [contactNumber, setContactNumber] = shopCtx.contactNumber
  const [transportationProduct, setTransportationProduct] = shopCtx.transportationProduct
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  if (!selectedPolicyId) throw Error('No policy selected!')

  const { data: policyData } = useQuery(queries.policyDetails(selectedPolicyId), () =>
    ecomPolicyResources.getPolicyDetails(selectedPolicyId)
  )

  const { data } = useQuery(
    queries.shopAddressesSetup(),
    () => {
      if (!policyData?.vendorId) throw Error('Policy data is missing!')
      return ecomPolicyResources.getShopAddressSetup(policyData.vendorId, costObjectId)
    },
    {
      enabled:
        Boolean(selectedPolicyId) &&
        Boolean(costObjectId) &&
        Boolean(policyData) &&
        Boolean(policyData?.vendorId),
    }
  )

  const { data: policyProducts } = useQuery(
    queries.policyProducts(selectedPolicyId),
    () => {
      if (!policyData) throw Error('Policy data is missing!')
      return ecomPolicyResources.getPolicyProducts(selectedPolicyId, policyData.vendorId)
    },
    { enabled: !!policyData }
  )
  const { data: employeeData } = useQuery(commonQueries.employees(), () =>
    employeeResources.getList(isAdmin)
  )
  const selectedLocationCostObjectId = employeeData?.locationList.find(
    location => location.id === costObjectId
  )

  if (!data) return null

  const isAddressSelectVisible = data?.length > 0
  const isAddressInputsVisible = !isAddressSelectVisible || selectedAddressId === CUSTOM_ADDRESS_ID

  let addressOptions = []
  if (policyData?.isCustomAddressActivated) {
    addressOptions.push({
      value: CUSTOM_ADDRESS_ID,
      label: translate('Custom address'),
    })
  }
  addressOptions = [
    ...addressOptions,
    ...data.map(address => ({
      value: address.id,
      label: `${address.address}, ${address.zip} ${address.city}`,
    })),
  ]

  function handleSelectTransportationProduct(postcode: string) {
    const selectedTransportationProduct = getTransportationProduct({
      selectedPostcode: postcode,
      transportationProducts: policyProducts?.transportationProducts || [],
    })
    setTransportationProduct(selectedTransportationProduct || null)
    // Clean up
    if (transportationProduct) {
      setProductQuantityById({ ...productQuantityById, [transportationProduct.id]: 0 })
    }
    if (selectedTransportationProduct) {
      setProductQuantityById({ ...productQuantityById, [selectedTransportationProduct.id]: 1 })
    }
  }

  const handleSelectAddress = (option: ValueLabelPair | null) => {
    if (!option) throw Error('No option selected!')
    if (typeof option.value === 'string') throw Error('Incorrect address id!')
    setSelectAddressId(option.value)
    if (option.value === CUSTOM_ADDRESS_ID) {
      if (customAddress?.zip) handleSelectTransportationProduct(customAddress?.zip)
      setIsPrivateDeliveryAddress(true)
      return
    }
    const postcode = data.find(address => address.id === option.value)?.zip
    if (!postcode) throw Error('Postcode not found!')
    handleSelectTransportationProduct(postcode)
    setIsPrivateDeliveryAddress(false)
  }

  return (
    <section className="p-4">
      {isAddressSelectVisible && (
        <Select
          id="delivery-address"
          placeholder={translate('Select delivery address')}
          label={<Translation id="Delivery address" />}
          className="react-select"
          marginBottom={16}
          options={addressOptions}
          onChange={handleSelectAddress}
          value={selectedAddressId || undefined}
        />
      )}

      <div
        className={twJoin(
          'max-mobile:flex-col my-2 flex-wrap gap-1 overflow-hidden transition sm:gap-2',
          isAddressInputsVisible ? 'flex' : 'hidden'
        )}
      >
        <div className="basis-full">
          <Input
            id="address"
            className="m-0 w-full"
            label={<Translation id="Address" />}
            value={customAddress?.address || ''}
            onChange={e => setCustomAddress({ ...(customAddress || {}), address: e.target.value })}
          />
        </div>

        <div className="basis-1/3">
          <Input
            id="zip"
            className="m-0 w-full"
            label={<Translation id="Zip" />}
            value={customAddress?.zip || ''}
            maxLength={10}
            onChange={e => {
              setCustomAddress({ ...(customAddress || {}), zip: e.target.value })
              handleSelectTransportationProduct(e.target.value)
            }}
          />
        </div>

        <div className="grow">
          <InputWithValidation
            id="city"
            className="m-0 w-full"
            errorMessage={<Translation id="City name may only include letters" />}
            validator={isOnlyLetters}
            label={<Translation id="City" />}
            value={customAddress?.city || ''}
            onChange={e => setCustomAddress({ ...(customAddress || {}), city: e.target.value })}
          />
        </div>
      </div>

      {selectedAddressId === CUSTOM_ADDRESS_ID && (
        <RadioGroup id="delivery-address-type" className="my-4">
          <div className="mt-1 flex flex-col gap-2">
            <Radio
              id="is-business-address"
              checked={!isPrivateDeliveryAddress}
              onChange={() => {
                setIsPrivateDeliveryAddress(false)
              }}
              onClick={() => {
                setIsPrivateDeliveryAddress(false)
              }}
              label={<Translation id="This is a business address" />}
            />

            <Radio
              id="is-private-address"
              checked={isPrivateDeliveryAddress}
              onChange={() => {
                setIsPrivateDeliveryAddress(true)
                if (selectedAddressId === null || isAddressInputsVisible) {
                  setSelectAddressId(CUSTOM_ADDRESS_ID)
                }
              }}
              onClick={() => {
                setIsPrivateDeliveryAddress(true)
                if (selectedAddressId === null || isAddressInputsVisible) {
                  setSelectAddressId(CUSTOM_ADDRESS_ID)
                }
              }}
              label={<Translation id="This is a private address" />}
            />
          </div>
        </RadioGroup>
      )}

      {Boolean(selectedLocationCostObjectId) && (
        <>
          <Input
            id="contactName"
            isFullWidth
            label={
              <>
                <Translation id="Contact name" />*
              </>
            }
            value={contactName || ''}
            onChange={e => setContactName(e.target.value)}
          />

          <div className="mt-2">
            <Label>
              <Translation id="Contact number" />*
            </Label>

            <div className="flex items-center gap-2">
              <Select
                value={contactNumber.phoneCountryCode || undefined}
                id="phoneCountryId"
                options={countryOptions.map(option => ({
                  value: option.value,
                  label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                }))}
                className="w-fit"
                onChange={option => {
                  let formattedNumber = contactNumber?.phoneNumber || ''
                  if (contactNumber?.phoneNumber && option?.value) {
                    formattedNumber = getFormattedPhoneNumber({
                      value: contactNumber?.phoneNumber,
                      countryId: option?.value,
                    })
                  }
                  setContactNumber({
                    phoneNumber: formattedNumber || '',
                    phoneCountryCode: option?.value || null,
                  })
                }}
              />

              <Input
                isFullWidth
                id="contactNumber"
                value={contactNumber?.phoneNumber || ''}
                wrapperClassName="grow"
                maxLength={MAX_PHONE_NUMBER_LENGTH}
                onChange={e => {
                  let formattedNumber = e.target.value
                  if (contactNumber?.phoneCountryCode) {
                    formattedNumber = getFormattedPhoneNumber({
                      value: e.target.value,
                      countryId: contactNumber?.phoneCountryCode,
                    })
                  }
                  setContactNumber({
                    phoneCountryCode: contactNumber?.phoneCountryCode || null,
                    phoneNumber: formattedNumber,
                  })
                }}
              />
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default SectionAddress
